import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { fromJS, List } from "immutable";
import { createSelector } from "reselect";
import { getCurrentLocale, newTranslate } from "../l10n/store.js";
import { mappedTranslate } from "../l10n/util.js";
import { getCurrentDateTime } from "../time/store.js";
import { setTime } from "../util/date.js";
import { findStation, stationToMaxBookingPeriodRule, stationToMaxLeadTimeRule, stationToMinBookingPeriodRule, stationToMinLeadTimeRule, stationToOpeningTimes } from "./util.js";
import { dateError, dateTimeError } from "./validation.js";
export function sharedStationReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;
  switch (action.type) {
    case "STATIONS/STATIONS_LOADED":
      return _objectSpread(_objectSpread({}, state), {}, {
        stations: action.payload.stations,
        stationsCacheKey: action.payload.cacheKey,
        stationsLocale: action.payload.locale,
        stationsLoaded: true
      });
    default:
      return state;
  }
}
export var areStationsLoaded = function areStationsLoaded(cacheKey) {
  return function (state) {
    var _state$shared_station, _state$shared_station2, _state$shared_station3;
    return ((state === null || state === void 0 || (_state$shared_station = state.shared_stations) === null || _state$shared_station === void 0 ? void 0 : _state$shared_station.stationsLoaded) || false) && (state === null || state === void 0 || (_state$shared_station2 = state.shared_stations) === null || _state$shared_station2 === void 0 ? void 0 : _state$shared_station2.stationsLocale) === getCurrentLocale(state) && (state === null || state === void 0 || (_state$shared_station3 = state.shared_stations) === null || _state$shared_station3 === void 0 ? void 0 : _state$shared_station3.stationsCacheKey) === cacheKey;
  };
};
var getRawStations = function getRawStations(state) {
  var _state$shared_station4;
  return state === null || state === void 0 || (_state$shared_station4 = state.shared_stations) === null || _state$shared_station4 === void 0 ? void 0 : _state$shared_station4.stations;
};
export var getStations = createSelector([getRawStations], function (stations) {
  return stations ? fromJS(stations) : List();
});
export var getValidateCoStationId = function getValidateCoStationId(codeMapping) {
  return createSelector([newTranslate, getStations], function (baseTranslate, stations) {
    var translate = mappedTranslate(baseTranslate, codeMapping);
    return function (coStationId) {
      var coStation = findStation(stations, coStationId);
      if (!coStation) {
        return translate("CO_STATION_MISSING");
      }
      return undefined;
    };
  });
};
export var getValidateCoDateTime = function getValidateCoDateTime(codeMapping, dataMapping) {
  return createSelector([newTranslate, getCurrentDateTime, getStations], function (baseTranslate, currentDateTime, stations) {
    var translate = mappedTranslate(baseTranslate, codeMapping, dataMapping);
    return function (coDateTime, coStationId) {
      var coStation = findStation(stations, coStationId);
      return dateTimeError({
        dateTime: coDateTime,
        currentDateTime: currentDateTime,
        periodStartDateTime: currentDateTime,
        minPeriodRule: stationToMinLeadTimeRule(coStation),
        maxPeriodRule: stationToMaxLeadTimeRule(coStation),
        openingTimes: stationToOpeningTimes(coStation),
        translate: translate
      });
    };
  });
};

// Returns true when coStation is open at the given dateTime and given
// dateTime satisfies minLeadTime and maxLeadTime.
export var getNewIsValidCoDateTime = createSelector([getValidateCoDateTime()], function (validateCoDateTime) {
  return function (coStationId) {
    return function (coDateTime) {
      return validateCoDateTime(coDateTime, coStationId) === undefined;
    };
  };
});
export var getValidateCiDateTime = function getValidateCiDateTime(codeMapping, dataMapping) {
  return createSelector([newTranslate, getCurrentDateTime, getStations], function (baseTranslate, currentDateTime, stations) {
    var translate = mappedTranslate(baseTranslate, codeMapping, dataMapping);
    return function (ciDateTime, coStationId, ciStationId, coDateTime) {
      var coStation = findStation(stations, coStationId);
      var ciStation = findStation(stations, ciStationId);
      return dateTimeError({
        dateTime: ciDateTime,
        currentDateTime: currentDateTime,
        periodStartDateTime: coDateTime,
        minPeriodRule: stationToMinBookingPeriodRule(coStation),
        maxPeriodRule: stationToMaxBookingPeriodRule(coStation),
        openingTimes: stationToOpeningTimes(ciStation),
        translate: translate
      });
    };
  });
};

// Returns true when ciStation is open at the given dateTime and given
// dateTime satisfies minBookingPeriod and maxBookingPeriod.
export var getNewIsValidCiDateTime = createSelector([getValidateCiDateTime()], function (validateCiDateTime) {
  return function (coStationId, ciStationId, coDateTime) {
    return function (ciDateTime) {
      return validateCiDateTime(ciDateTime, coStationId, ciStationId, coDateTime) === undefined;
    };
  };
});

// A coDate is valid when the station is open at any time on this day
// and midnight on this day is between minLeadTime and maxLeadTime.
// This is because we use midnight of the selected day as coDateTime
// when creating the rentContract.
//
// To accomplish this we first use `dateError` to check whether the
// station is open on the given date at all.  Second we call
// `dateTimeError` to check the future coDateTime against minLeadTime
// and maxLeadTime while ignoring the actual openingTimes.
export var getValidateCoDate = function getValidateCoDate(codeMapping, dataMapping) {
  return createSelector([newTranslate, getCurrentDateTime, getStations], function (baseTranslate, currentDateTime, stations) {
    var translate = mappedTranslate(baseTranslate, codeMapping, dataMapping);
    return function (coDate, coStationId) {
      var coStation = findStation(stations, coStationId);
      return dateError({
        date: coDate,
        currentDateTime: currentDateTime,
        openingTimes: stationToOpeningTimes(coStation),
        translate: translate
      }) || dateTimeError({
        dateTime: setTime(coDate, "00:00"),
        currentDateTime: currentDateTime,
        periodStartDateTime: currentDateTime,
        minPeriodRule: stationToMinLeadTimeRule(coStation),
        maxPeriodRule: stationToMaxLeadTimeRule(coStation),
        translate: translate
      });
    };
  });
};

// Returns true when coStation is open at the given date AND midnight
// of the given date satisfies minLeadTime and maxLeadTime.
export var getNewIsValidCoDate = createSelector([getValidateCoDate()], function (validateCoDate) {
  return function (coStationId) {
    return function (coDate) {
      return validateCoDate(coDate, coStationId) === undefined;
    };
  };
});

// A ciDate is valid when the station is open at any time on this day
// and midnight on this day is between minBookingPeriod and
// maxBookingPeriod.  This is because we use midnight of the selected
// day as ciDateTime when creating the rentContract.
//
// To accomplish this we first use `dateError` to check whether the
// station is open on the given date at all.  Second we call
// `dateTimeError` to check the future ciDateTime against
// minBookingPeriod and maxBookingPeriod while ignoring the actual
// openingTimes.
export var getValidateCiDate = function getValidateCiDate(codeMapping, dataMapping) {
  return createSelector([newTranslate, getCurrentDateTime, getStations], function (baseTranslate, currentDateTime, stations) {
    var translate = mappedTranslate(baseTranslate, codeMapping, dataMapping);
    return function (ciDate, coStationId, ciStationId, coDateTime) {
      var coStation = findStation(stations, coStationId);
      var ciStation = findStation(stations, ciStationId);
      return dateError({
        date: ciDate,
        currentDateTime: currentDateTime,
        openingTimes: stationToOpeningTimes(ciStation),
        translate: translate
      }) || dateTimeError({
        dateTime: setTime(ciDate, "23:59:59"),
        currentDateTime: currentDateTime,
        periodStartDateTime: coDateTime,
        minPeriodRule: stationToMinBookingPeriodRule(coStation),
        maxPeriodRule: stationToMaxBookingPeriodRule(coStation),
        translate: translate
      });
    };
  });
};

// Returns true when ciStation is open at the given date AND midnight
// of the given date satisfies minBookingPeriod and maxBookingPeriod.
export var getNewIsValidCiDate = createSelector([getValidateCiDate()], function (validateCiDate) {
  return function (coStationId, ciStationId, coDateTime) {
    return function (ciDate) {
      return validateCiDate(ciDate, coStationId, ciStationId, coDateTime) === undefined;
    };
  };
});

// Returns true when coStation is open between minLeadTime and
// maxLeadTime on the given date.
export var getNewDateContainsValidCoDateTime = createSelector([getStations, getCurrentDateTime], function (stations, currentDateTime) {
  return function (coStationId) {
    var coStation = findStation(stations, coStationId);

    // It does not matter what the return value of the `translate`
    // function is because we only care if the validation result is
    // `undefined` and not about any particular error message.
    var translate = function translate() {
      return "irrelevant message";
    };
    return function (date) {
      return dateError({
        date: date,
        currentDateTime: currentDateTime,
        periodStartDateTime: currentDateTime,
        minPeriodRule: stationToMinLeadTimeRule(coStation),
        maxPeriodRule: stationToMaxLeadTimeRule(coStation),
        openingTimes: stationToOpeningTimes(coStation),
        translate: translate
      }) === undefined;
    };
  };
});

// Returns true when ciStation is open between minBookingPeriod and
// maxBookingPeriod on the given date.
export var getNewDateContainsValidCiDateTime = createSelector([getStations, getCurrentDateTime], function (stations, currentDateTime) {
  return function (coStationId, ciStationId, coDateTime) {
    var coStation = findStation(stations, coStationId);
    var ciStation = findStation(stations, ciStationId);

    // It does not matter what the return value of the `translate`
    // function is because we only care if the validation result is
    // `undefined` and not about any particular error message.
    var translate = function translate() {
      return "irrelevant message";
    };
    return function (date) {
      return dateError({
        date: date,
        currentDateTime: currentDateTime,
        periodStartDateTime: coDateTime,
        minPeriodRule: stationToMinBookingPeriodRule(coStation),
        maxPeriodRule: stationToMaxBookingPeriodRule(coStation),
        openingTimes: stationToOpeningTimes(ciStation),
        translate: translate
      }) === undefined;
    };
  };
});